import React from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Icon,
  Text,
  Stack,
  PseudoBox,
} from '@chakra-ui/core';
import Can from '../../../roleBasedAccess/Can';
import routes from '../../routes';
import { canManageQuestions } from '../../../quotes/questionHelpers';

const MainNav = ({ isOpen = false, onClose = () => {} }) => {
  const [isDesktopExpanded, setIsDesktopExpanded] = React.useState(false);

  const handleDesktopBlur = event => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDesktopExpanded(false);
    }
  };

  return (
    <React.Fragment>
      <Box
        data-testid="desktop-main-nav"
        data-expanded={isDesktopExpanded}
        display={{ base: 'none', lg: 'block' }}
        bg="semiDarkGray.900"
        width="sidebar"
        minW="sidebar"
        flexShrink={0}
        alignSelf="stretch"
        position="relative"
        zIndex="sticky"
        onMouseEnter={() => setIsDesktopExpanded(true)}
        onMouseLeave={() => setIsDesktopExpanded(false)}
        onFocus={() => setIsDesktopExpanded(true)}
        onBlur={handleDesktopBlur}
      >
        <Box
          aria-hidden="true"
          data-testid="desktop-main-nav-backdrop"
          display={{ base: 'none', lg: 'block' }}
          position="fixed"
          top={0}
          bottom={0}
          left={0}
          width={isDesktopExpanded ? '290px' : 'sidebar'}
          bg="semiDarkGray.900"
          boxShadow={isDesktopExpanded ? '8px 0 24px rgba(20, 24, 28, 0.24)' : 'none'}
          transition="width 0.28s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease"
          zIndex={0}
        />
        <PseudoBox
          as="nav"
          aria-label="Navigation principale"
          data-testid="desktop-main-nav-content"
          data-expanded={isDesktopExpanded}
          bg="semiDarkGray.900"
          color="white"
          px={4}
          pt={6}
          pb={6}
          transition="width 0.28s cubic-bezier(0.4, 0, 0.2, 1)"
          width={isDesktopExpanded ? '290px' : 'sidebar'}
          position="sticky"
          top={0}
          zIndex={1}
          css={theme => css`
            &[data-expanded='true'] {
              ul {
                align-items: stretch;
              }

              [data-main-nav-icon] {
                margin-right: ${theme.space[4]};
              }

              [data-main-nav-label] {
                width: 180px;
                opacity: 1;
                transform: translateX(0);
              }
            }

            @media (prefers-reduced-motion: reduce) {
              &,
              [data-main-nav-icon],
              [data-main-nav-label] {
                transition: none;
              }
            }
          `}
        >
          <MainNavLinks />
        </PseudoBox>
      </Box>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="280px" isFullHeight>
        <DrawerOverlay />
        <DrawerContent bg="semiDarkGray.900" color="white" h="100vh">
          <DrawerCloseButton color="white" top={4} right={4} />
          <DrawerHeader borderBottom="1px solid" borderColor="whiteAlpha.200" px={5} py={5}>
            <Text fontFamily="heading" fontSize="lg" fontWeight="bold" color="white">
              Navigation
            </Text>
          </DrawerHeader>
          <DrawerBody p={0} overflowY="auto">
            <Box as="nav" aria-label="Navigation mobile" px={4} py={5}>
              <MainNavLinks expanded onNavigate={onClose} />
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </React.Fragment>
  );
};

const MainNavLinks = ({ expanded = false, onNavigate }) => {
  const authentication = useSelector(state => state.authentication || {});
  const currentUser =
    Object.keys(authentication.profile || {}).length > 0
      ? authentication.profile
      : authentication.user || {};
  const showFolders =
    currentUser.role !== 'coordinateur entreprises' ||
    process.env.REACT_APP_COMPANY_COORDINATOR_FOLDERS_ENABLED === 'true';

  return (
    <Stack
      as="ul"
      listStyleType="none"
      alignItems={expanded ? 'stretch' : 'start'}
      onClick={onNavigate}
    >
      <Can
        perform="dashboard:visit"
        yes={() => (
          <MainNavLink to={routes.dashboard} icon="dashboard" expanded={expanded}>
            Dashboard
          </MainNavLink>
        )}
      />
      <Can
        perform="report:read"
        yes={() => (
          <MainNavLink to={routes.reporting} icon="report-file" expanded={expanded}>
            Reporting
          </MainNavLink>
        )}
      />
      <Can
        perform="beneficiaries:read"
        yes={() => (
          <MainNavLink to={routes.recipients} icon="beneficiaries" expanded={expanded}>
            Bénéficiaires
          </MainNavLink>
        )}
      />
      <Can
        perform="pre-folder:edit"
        yes={() => (
          <MainNavLink to={routes.preFolders} icon="pre-folder" expanded={expanded}>
            Pré-dossiers
          </MainNavLink>
        )}
      />
      {showFolders && (
        <Can
          perform="folder:read"
          yes={() => (
            <MainNavLink to={routes.folders.index} icon="folder" expanded={expanded}>
              Dossiers
            </MainNavLink>
          )}
          no={() => (
            <Can
              perform="folder:bloc:read"
              yes={() => (
                <MainNavLink to={routes.folders.index} icon="folder" expanded={expanded}>
                  Dossiers
                </MainNavLink>
              )}
            />
          )}
        />
      )}
      <Can
        perform="invoices:read"
        yes={() => (
          <MainNavLink
            to={`${routes.invoices.index}${routes.invoices.types[0]}`}
            icon="pre-folder"
            expanded={expanded}
          >
            Factures
          </MainNavLink>
        )}
      />
      <Can
        perform="project-place-work:read"
        yes={() => (
          <MainNavLink to={routes.projectPlace} icon="megaphone" expanded={expanded}>
            Project Place
          </MainNavLink>
        )}
        no={() => (
          <Can
            perform="project-place-ergo:read"
            yes={() => (
              <MainNavLink to={routes.projectPlace} icon="megaphone" expanded={expanded}>
                Project Place
              </MainNavLink>
            )}
          />
        )}
      />
      <Can
        perform="quality-control-summaries:read"
        yes={() => (
          <MainNavLink to={routes.qualityControlSummaries} icon="search" expanded={expanded}>
            Controle qualité
          </MainNavLink>
        )}
      />
      <Can
        perform="expected-quotes:read"
        yes={() => (
          <MainNavLink to={routes.expectedQuotes} icon="expected-quotes" expanded={expanded}>
            Devis attendus
          </MainNavLink>
        )}
      />
      <Can
        perform="quotes:read"
        yes={() => (
          <MainNavLink
            to={`${routes.quotes.index}${routes.quotes.types[0]}`}
            icon="quotes"
            expanded={expanded}
          >
            Suivi des devis
          </MainNavLink>
        )}
      />
      {canManageQuestions(currentUser) && (
        <MainNavLink to={routes.quotes.questions} icon="comment" expanded={expanded}>
          Questions entreprises
        </MainNavLink>
      )}
      <Can
        perform="tasks:read"
        yes={() => (
          <MainNavLink to={routes.tasks.index} icon="tasks" expanded={expanded}>
            Tâches
          </MainNavLink>
        )}
      />
      <Can
        perform="sectors:read"
        yes={() => (
          <MainNavLink to={routes.sectors.index} icon="map" expanded={expanded}>
            Secteurs
          </MainNavLink>
        )}
      />
      <Can
        perform="users:read"
        yes={() => (
          <MainNavLink to={routes.users} icon="users" expanded={expanded}>
            {currentUser.role === 'coordinateur entreprises'
              ? 'Référents entreprise'
              : 'Opérateurs'}
          </MainNavLink>
        )}
      />
      <Can
        perform="companies:read"
        yes={() => (
          <MainNavLink to={routes.companies} icon="companies" expanded={expanded}>
            Entreprises
          </MainNavLink>
        )}
      />
      <Can
        perform="ergo-offices:read"
        yes={() => (
          <MainNavLink to={routes.ergoOffices} icon="offices" expanded={expanded}>
            Cabinets
          </MainNavLink>
        )}
      />
      <Can
        perform="ergo-resources:read"
        yes={() => (
          <MainNavLink to={routes.ergoResources} icon="resource" expanded={expanded}>
            Ressources
          </MainNavLink>
        )}
      />
    </Stack>
  );
};

const MainNavLink = ({ icon, to, children, exact, expanded = false }) => {
  return (
    <Box as="li" mb={5}>
      <NavLink
        to={to}
        exact={exact}
        css={theme =>
          css`
            display: block;
            &,
            svg {
              transition: 0.3s ease;
            }
            p {
              position: relative;
              isolation: isolate;

              &::before {
                content: '';
                position: absolute;
                top: -12px;
                right: 0;
                bottom: -12px;
                left: 0;
                z-index: -1;
                border-radius: 9999px;
                background-color: ${theme.colors.whiteAlpha[200]};
                opacity: 0;
                transform: scaleX(0.96);
                transition: opacity 0.16s ease, transform 0.2s ease;
                pointer-events: none;
              }
            }
            &.active {
              p {
                background-color: ${theme.colors.whiteAlpha[200]};
                font-weight: 600;
                font-size: ${theme.fontSizes.lg};
                padding: ${theme.space[4]};
                min-height: 62px;
                svg {
                  opacity: 1;
                  color: ${theme.colors.white};
                  width: 30px;
                  height: auto;
                  max-height: 30px;
                }
              }
            }
            &:not(.active):hover {
              p {
                color: ${theme.colors.white};

                &::before {
                  opacity: 1;
                  transform: scaleX(1);
                }
              }
              svg {
                opacity: 0.6;
              }
            }
          `
        }
      >
        <Box
          as="p"
          whiteSpace="nowrap"
          d="flex"
          alignItems="center"
          rounded="full"
          color="whiteAlpha.800"
          fontFamily="heading"
          px="1.2rem"
          py={expanded ? 3 : 0}
          minH={expanded ? '48px' : undefined}
          overflow="visible"
        >
          <Icon
            name={icon}
            data-main-nav-icon
            size={6}
            color="white"
            opacity="0.4"
            mr={expanded ? 4 : 0}
            flexShrink={0}
          />
          <Text
            as="span"
            data-main-nav-label
            d="block"
            width={expanded ? 'auto' : '0'}
            opacity={expanded ? '1' : '0'}
            overflow="hidden"
            transform={expanded ? 'translateX(0)' : 'translateX(-6px)'}
            transition="width 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.16s ease, transform 0.2s ease"
          >
            {children}
          </Text>
        </Box>
      </NavLink>
    </Box>
  );
};

export default MainNav;
